import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { StaffService } from './staff.service';
import { Staff } from '../../interfaces/institution/staff/staff.interface';
import { environment } from '../../../../../environments/environment.dev';
import { StaffSearchParam } from '../../interfaces/institution/staff/staff-search-param.interface';
import { convertToHttpParams } from '../../../../core/interfaces/common-search-param.interface';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';

@Injectable({
    providedIn: 'root'
})
export class StaffApiService extends StaffService {
    private http = inject(HttpClient);
    private readonly baseUrl = `${environment.apiBaseUrl}v1/staff`;

    constructor() {
        super();
    }

    override findAll(searchParam: StaffSearchParam): Observable<PageResponse<Staff>> {
        let url = this.baseUrl;
        if (searchParam.institutionUniqueId) {
            url = `${environment.apiBaseUrl}v1/institutions/staffs`;
        }

        return this.wrapRequest(
            this.http.get<any>(url, {
                params: convertToHttpParams(searchParam),
                headers: this.headers
            }).pipe(
                map(res => {
                    const data = res.response || res;
                    return {
                        ...data,
                        content: data.content?.map((staff: any) => ({
                            ...staff,
                            phoneNo: staff.phoneNo || staff.phone || ''
                        })) || []
                    };
                })
            )
        );
    }

    override find(id: string): Observable<Staff> {
        return this.wrapRequest(
            this.http.get<Staff>(`${this.baseUrl}/${id}`, { headers: this.headers })
        );
    }

    override create(data: Partial<Staff>): Observable<Staff> {
        let url = this.baseUrl;
        if (data.institutionUniqueId) {
            url = `${environment.apiBaseUrl}v1/institutions/${data.institutionUniqueId}/staffs`;
        }
        return this.wrapRequest(
            this.http.post<Staff>(url, data, { headers: this.headers })
        );
    }

    override update(id: string, data: Partial<Staff>): Observable<Staff> {
        let url = `${this.baseUrl}/${id}`;
        if (data.institutionUniqueId) {
            url = `${environment.apiBaseUrl}v1/institutions/${data.institutionUniqueId}/staffs/${id}`;
        }
        return this.wrapRequest(
            this.http.put<Staff>(url, data, { headers: this.headers })
        );
    }

    override delete(id: string): Observable<void> {
        return this.wrapRequest(
            this.http.delete<void>(`${this.baseUrl}/${id}`, { headers: this.headers })
        );
    }

    override inviteStaff(staff: Partial<Staff>[]): Observable<any> {
        const firstStaff = staff[0];
        let url = this.baseUrl;

        if (firstStaff?.institutionUniqueId) {
            url = `${environment.apiBaseUrl}v1/institutions/${firstStaff.institutionUniqueId}/staffs`;
        }

        const payload = staff.map(s => {
            return {
                fullName: s.fullName,
                email: s.email,
                phoneNo: s.phoneNo
            };
        });

        return this.wrapRequest(
            this.http.post<any>(url, payload, { headers: this.headers })
        );
    }

    override uploadStaffCSV(institutionId: string, file: File): Observable<Staff[]> {
        const url = `${environment.apiBaseUrl}v1/institutions/${institutionId}/staffs/upload`;
        const formData = new FormData();
        formData.append('file', file);

        return this.wrapRequest(
            this.http.post<Staff[]>(url, formData, {
                headers: this.headers.delete('Content-Type')
            })
        );
    }

    override updateStatus(id: string, status: boolean): Observable<void> {
        const url = `${environment.apiBaseUrl}v1/institutions/staffs/${id}/status`;
        return this.wrapRequest(
            this.http.patch<void>(url, null, {
                headers: this.headers,
                params: { isActive: status.toString() }
            })
        );
    }

    override updatePassword(uniqueId: string, password: string): Observable<void> {
        const payload = { staffUniqueId: uniqueId, password };
        return this.wrapRequest(
            this.http.put<void>(`${this.baseUrl}/password`, payload, { headers: this.headers })
        );
    }
}
